import { screenshareStopped } from 'features/screenshare/actions';
import { getDisplayMedia } from 'features/user-media/utils/getDisplayMedia';
import { selectLocalUserId } from 'features/users/usersSlice';
import { selectScreenshareStreams } from 'features/layout/features/order/orderSlice';
import { contentBoard } from 'features/content-whiteboard/utils/contentBoardStateManager';
import { store } from 'store/store';
import { logger } from 'utils/logger';
import { FeedMediaState, RTCClient } from 'utils/webrtc/index';
import { BasePublishing } from 'utils/webrtc/publishing';
import { screenshareSimulcastMaxBitrates } from 'utils/webrtc/environment';
import type { MobileScreenshareData } from 'utils/eventBus';

export class ScreensharingFeed extends BasePublishing {
  defaultMediaState: FeedMediaState = {
    enabled: true,
    allowed: true,
    broadcasting: false,
    captured: false,
  };

  mediaStates = {
    video: { ...this.defaultMediaState },
    audio: { ...this.defaultMediaState },
  };

  savedStream: MediaStream | null = null;

  pendingReconnect: boolean = false;

  isReconnecting: boolean = false;

  mobileScreenshareEnabled: boolean = false;

  private visibilityHandler: (() => void) | null = null;

  constructor() {
    super('screensharing');
  }

  getTrackStates = () =>
    this.savedStream?.getTracks().map((t) => `${t.kind}:${t.readyState}`) ?? [];

  private attachVisibilityListener = () => {
    if (this.visibilityHandler) {
      return;
    }

    this.visibilityHandler = () => {
      if (this.broadcastIntended) {
        logger.remote().debug('[SCREENSHARE-DEBUG] visibilitychange', {
          hidden: document.hidden,
          tracks: this.getTrackStates(),
        });
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  };

  private removeVisibilityListener = () => {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  };

  getActiveMediaConfig = () => {
    if (this.savedStream) {
      const clonedStream = new MediaStream();

      this.savedStream.getTracks().forEach((track) => clonedStream.addTrack(track.clone()));

      this.mediaController.storedMediaStreams[clonedStream.id] = clonedStream;

      return {
        stream: clonedStream,
      };
    }

    if (this.broadcastIntended) {
      logger.remote().error(`Missing stored stream in screensharing feed`);
    }

    return {
      video: false,
      audio: false,
    };
  };

  protected getSimulcastMaxBitrates = () => screenshareSimulcastMaxBitrates;

  protected getSendEncodings = () => {
    const enableSimulcast = this.isSimulcastEnabled();

    if (!enableSimulcast) {
      return null;
    }

    const bitrates = this.getSimulcastMaxBitrates();

    const encodings = {
      h: { rid: 'h', active: true, maxBitrate: bitrates.high },
      l: {
        rid: 'l',
        active: enableSimulcast,
        maxBitrate: bitrates.low,
        scaleResolutionDownBy: 3,
      },
    };

    return Object.values(encodings);
  };

  protected isSimulcastEnabled = () => {
    const state = store.getState();
    const screenshares = selectScreenshareStreams(state);
    return screenshares.length > 5;
  };

  captureScreensharingState = () => {
    const { broadcastIntended, savedStream } = this;

    if (broadcastIntended && savedStream) {
      return { broadcastIntended, savedStream };
    }

    return null;
  };

  startMobileScreenshare = (data: MobileScreenshareData) => {
    const isVideoStreamExist = data.streams.some((stream) => stream.type === 'video');

    data.streams.forEach((stream) => {
      this.mediaStates[stream.type].broadcasting = true;
    });

    if (isVideoStreamExist) {
      this.broadcastIntended = true;
      this.mobileScreenshareEnabled = true;

      RTCClient.setupLocalFeed({
        id: data.feedId,
        private_id: RTCClient.privateId || 0, // TODO check the value
      });
    }

    this.signalingBus.addMessage('start', data.feedId, data.streams);

    this.updateRedux();
  };

  stopMobileScreenshare = (data: MobileScreenshareData) => {
    const isVideoStreamExist = data.streams.some((stream) => stream.type === 'video');

    data.streams.forEach((stream) => {
      this.mediaStates[stream.type].broadcasting = false;
    });

    if (isVideoStreamExist) {
      this.broadcastIntended = false;
      this.mobileScreenshareEnabled = false;
    }

    this.signalingBus.addMessage('stop', data.feedId, data.streams);

    this.updateRedux();
  };

  shareScreen = async () => {
    logger.remote().debug('[SCREENSHARE-DEBUG] shareScreen called', {
      pendingReconnect: this.pendingReconnect,
      hasHandle: !!this.handle,
      hasSavedStream: !!this.savedStream,
      tracks: this.getTrackStates(),
    });

    this.broadcastIntended = true;

    // Publishing connection is torn down during reconnect; defer the attach so
    // attemptPublish resumes it once the connection is re-established.
    if (!RTCClient.mediaServerConnector.connections.publishing) {
      logger
        .remote()
        .warn('[SCREENSHARE-DEBUG]: deferring shareScreen, publishing connection absent');
      this.pendingReconnect = true;
      return;
    }

    if (this.pendingReconnect) {
      logger.remote().warn('[SCREENSHARE-DEBUG]: starting branch A', {
        hasSavedStream: !!this.savedStream,
        tracks: this.getTrackStates(),
      });
      this.cleanupConnection();
      this.attachPlugin();
      this.pendingReconnect = false;
      this.isReconnecting = true;
      logger.remote().warn('[SCREENSHARE-DEBUG]: starting branch A done');
    } else if (this.handle) {
      logger.remote().warn('[SCREENSHARE-DEBUG]: starting branch B', {
        feedId: this.handle.feedId,
      });

      this.resetMediaStates();
      // TODO: Maybe reset negotioation meta in here?; or maybe reset media states is not necessary

      await this.requestPublish({
        media: { replaceVideo: true, replaceAudio: true },
      });

      this.pendingReconnect = false;
      logger.remote().warn('[SCREENSHARE-DEBUG]: starting branch B done');
    } else {
      logger.remote().warn('[SCREENSHARE-DEBUG]: starting branch C', {
        hasSavedStream: !!this.savedStream,
      });

      this.attachPlugin();
      logger.remote().warn('[SCREENSHARE-DEBUG]: starting branch C done');
    }
  };

  stopScreenshare = async (notifySignaling: boolean = true) => {
    logger.remote().debug('[SCREENSHARE-DEBUG] stopScreenshare called', {
      notifySignaling,
      broadcastIntended: this.broadcastIntended,
      hasSavedStream: !!this.savedStream,
      hasHandle: !!this.handle?.feedId,
      hidden: document.hidden,
    });

    this.removeVisibilityListener();

    this.broadcastIntended = false;

    // Cancel a share deferred during a reconnect window so an explicit stop
    // isn't resurrected once the publishing connection returns.
    this.pendingReconnect = false;

    this.savedStream?.getTracks().forEach((track) => {
      track.stop();
    });

    this.savedStream = null;

    contentBoard.localScreenshareState = [];

    if (this.handle?.feedId) {
      if (notifySignaling) {
        this.signalingBus.addMessage(
          'stop',
          this.handle.feedId,
          Object.values(this.handle.streams)
        );
      }

      const userId = selectLocalUserId(store.getState());

      store.dispatch(
        screenshareStopped({
          id: userId,
          feedId: this.handle.feedId,
          streams: Object.values(this.handle.streams),
        })
      );
    }

    try {
      await this.requestPublish({
        media: {
          videoSend: false,
          removeVideo: true,
          audioSend: false,
          removeAudio: true,
        },
      });
    } catch (e) {
      // do nothing, it's ok
    }

    this.cleanupStreams();

    if (this.mediaStates.video.broadcasting) {
      this.stopVideoBroadcasting();
    } else {
      this.resetMediaStates('video');
    }

    if (this.mediaStates.audio.broadcasting) {
      this.stopAudioBroadcasting();
    } else {
      this.resetMediaStates('audio');
    }

    this.updateRedux();
  };

  toggleScreenshare = async () => {
    if (this.broadcastIntended) {
      await this.stopScreenshare();
    } else {
      await this.shareScreen();
    }
  };

  onMediaState = async (medium: 'audio' | 'video', on: boolean, mid?: string) => {
    logger
      .remote()
      .debug(`Janus ${on ? 'started' : 'stopped'} receiving our ${medium} (mid=${mid})`);

    const feed = this.handle;

    if (!feed) {
      return;
    }

    feed.streams = feed.streams || {};

    if (on) {
      RTCClient.resetPublishOptions();

      if (mid) {
        if (medium === 'audio' && this.negotiationMeta.pendingAudioBroadcastState !== null) {
          logger.warn(
            'ON_MEDIA_STATE tweaking audio with pending state...',
            this.negotiationMeta.pendingAudioBroadcastState
          );

          if (this.negotiationMeta.pendingAudioBroadcastState) {
            feed.videoroom.unmuteAudio(mid);
          } else {
            feed.videoroom.muteAudio(mid);
          }

          this.negotiationMeta.pendingAudioBroadcastState = null;
        }

        feed.streams[mid] = { type: medium, mid, on: true };
      }

      if (!feed.connected && !feed.connecting) {
        feed.connecting = true;
      }
    } else if (mid && feed.streams[mid]) {
      feed.streams[mid].on = false;
    }

    const shouldSendUpdate = on ? !this.mediaStates[medium].captured : false;

    this.mediaStates[medium].broadcasting = on;
    this.mediaStates[medium].captured = on;

    if (mid && shouldSendUpdate) {
      this.signalingBus.addMessage(on ? 'start' : 'stop', feed.feedId, [{ type: medium, mid }]);
    }

    this.updateRedux();
  };

  cleanupStreams = () => {
    Object.keys(this.mediaController.streams).forEach((id) => {
      this.mediaController.removeStream(id);
    });

    this.mediaController.clearStoredMediaStreams();
  };

  cleanupConnection = () => {
    logger.debug('Screensharing Feed: cleaning up connection');
    this.removeVisibilityListener();
    const feed = this.handle;
    this.resetMediaStates();

    this.handleCleanup();

    this.reconnectionReset();

    if (!feed) {
      return;
    }

    if (feed.feedId) {
      RTCClient.disconnectFeed(feed.feedId);
    }

    feed.videoroom.hangup();
  };

  attachEndedHandlers = () => {
    this.savedStream?.getTracks().forEach((track) => {
      track.onended = () => {
        logger.remote().debug('[SCREENSHARE-DEBUG] original track ended', {
          broadcastIntended: this.broadcastIntended,
          trackKind: track.kind,
          trackState: track.readyState,
          hidden: document.hidden,
        });

        if (this.broadcastIntended) {
          this.stopScreenshare();
        }
      };
    });
  };

  saveStream = async () => {
    this.savedStream = await getDisplayMedia();
    this.attachEndedHandlers();
    this.attachVisibilityListener();
  };
}
